import { Command } from "./Command";
import { HistoryEventHandler } from "../history";
import { ChangeElementCommand } from "./ChangeElementCommand";

/**
* History command for an element that was added to the DOM.
* @implements {module:history.HistoryCommand}
*/
export class InsertElementCommand extends Command {
    public parent: Element;
    public nextSibling: Element;

    /**
     * @param {SVGElement} elem - The newly added DOM element
     * @param {string} [text] - An optional string visible to user related to this change
    */
    constructor(elem: SVGElement, text?: string) {
        super();
        this.elem = elem;
        this.text = text || ('Create ' + elem.tagName);
        this.parent = elem.parentNode as Element;
        this.nextSibling = this.elem.nextSibling as Element;
    }

    /**
    * Re-inserts the new element.
    * @param {module:history.HistoryEventHandler} handler
    * @fires module:history~Command#event:history
    * @returns {void}
    */
    apply(handler: HistoryEventHandler) {
        super.apply(handler, () => {
            this.elem = this.parent.insertBefore(this.elem, this.nextSibling);
        });
    }

    /**
    * Removes the element.
    * @param {module:history.HistoryEventHandler} handler
    * @fires module:history~Command#event:history
    * @returns {void}
    */
    unapply(handler: HistoryEventHandler) {
        super.unapply(handler, () => {
            this.parent = this.elem.parentNode as Element;
            this.elem.remove();
        });
    }

    mergeHistory(other: Command, newText?: string): boolean {
        if (!(other instanceof ChangeElementCommand)) {
            return false;
        }

        if (other.elem !== this.elem) {
            return false;
        }

        // Are these changes ones that happen immediately after creation?
        if (Math.abs(other.timestamp.getTime() - this.timestamp.getTime()) > 500) {
            return false;
        }

        // The change command will make the necessary changes.
        // We return true so the change command doesn't go to the undo stack.
        // This command will still contain these changes on elem if undone and redone.

        return true;
    }
}
